import axios from 'axios';
import React, { useContext, useReducer } from 'react';
import { CSVTYPE, LITYPE } from '../../components/Utilities/NamingConversions';
import AuthContext from '../auth/AuthContext';
import MainContext from '../main/MainContext';
import LIContext from './LIContext';
import LIReducer from './LIReducer';
import { openPopup } from '@utils/popup';
import { EVENT_NAMES } from '@constants/trackingConstants';
import { v4 as uuidv4 } from 'uuid';
import { trackerService } from '@api/tracker';

function LIState(props) {
  const authContext = useContext(AuthContext);

  const mainContext = useContext(MainContext);
  const { getPostKWArgs, loadingIncrement, loadingDecrement, handleError } =
    mainContext;

  const initialState = {
    requestStatus: {
      oohPanels: 'IDLE',
      storeNames: 'IDLE',
      initialDates: 'IDLE',
    },
    driveTimeLayer: { type: 'FeatureCollection', features: [] },
    mapData: {},
    mapDataCache: {},
    geoLocation: null,
    oohData: {},
    oohPanelData: [],
    oohResults: [],
    storeName: [],
    topStoreName: [],
    storeData: [],
    kmlData: {},
    poiAnalytics: {},
    oohAnalytics: {},
    retailFlowMap: {},
    currPanel: {},
    popupView: false,
    storeCategories: [],
    singleCategories: [],
    brandNames: [],
    workflowId: null,
  };

  const [state, dispatch] = useReducer(LIReducer, initialState);

  const refresh = () => {
    dispatch({
      type: 'REFRESH',
      payload: {
        ...initialState,
        workflowId: state.workflowId,
      },
    });
  };

  // START -- API calls for first load into LI Module
  const getGeoLocation = async ({ type = LITYPE } = {}) => {
    // get geo location from ids if the ids are clustered around a small region
    // eg; if ids are mostly around North York, Markham and Toronto, return the
    // mode of the locations, otherwise, return null and rely on user's ip
    // (such as when audience is national)

    const getLocationFromIds = async () => {
      // Most accurate method, but relies on currently selected ids from mainContext
      try {
        const token = await authContext.getIdToken();
        const { data } = await axios.post(
          '/get_geo_location',
          {
            ids: mainContext.ids,
            countryCode: authContext.userCountryCode,
            kwargs: getPostKWArgs(),
          },
          {
            headers: { Authorization: token },
          }
        );
        return data ? data : null;
      } catch (err) {
        console.warn('Failed to get location from backend:', err);
        return null;
      }
    };

    const getLocationFromUserIP = async () => {
      // Fallback method - uses user's IP to determine location
      try {
        const { data } = await axios.get('https://ipapi.co/json/');
        return data.country.toLowerCase() === authContext.userCountryCode
          ? data
          : null;
      } catch (err) {
        console.warn('Failed to get location from ipapi.co:', err);
        return null;
      }
    };

    const getDefaultLocation = async () => {
      // Default location to use when no ids are provided or initial location cannot be determined
      return authContext.userCountryCode === 'ca'
        ? { latitude: 43.65107, longitude: -79.347015 } // Toronto
        : { latitude: 40.73061, longitude: -73.935242 }; // NYC
    };

    let initLocation = null;
    try {
      loadingIncrement(type);
      if (mainContext.ids) {
        // ids are provided, try to get initial location from the backend
        initLocation = await getLocationFromIds();
      }
      if (!initLocation) {
        // try to get initial location from user's IP
        initLocation = await getLocationFromUserIP();
      }
      if (!initLocation) {
        // get default location
        initLocation = await getDefaultLocation();
      }

      dispatch({
        type: 'SET_GEO_LOCATION',
        payload: {
          latitude: initLocation.latitude,
          longitude: initLocation.longitude,
        },
      });
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  };

  const setGeoLocation = ({ latitude, longitude } = {}) => {
    dispatch({
      type: 'SET_GEO_LOCATION',
      payload: { latitude, longitude },
    });
  };

  const fetchInitialDates = async () => {
    try {
      loadingIncrement(LITYPE);
      dispatch({
        type: 'GET_MOBILITY_DATES',
        payload: { requestStatus: 'PENDING' },
      });
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/search/initial_dates',
        {
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
        },
        { headers: { Authorization: token } }
      );
      dispatch({
        type: 'GET_MOBILITY_DATES',
        payload: { ...res.data, requestStatus: 'SUCCESS' },
      });
      return res.data;
    } catch (err) {
      if (err.response) {
        handleError(err);
        dispatch({
          type: 'GET_MOBILITY_DATES',
          payload: { requestStatus: 'ERROR' },
        });
      }
    } finally {
      loadingDecrement(LITYPE);
    }
  };
  // END -- API calls for first load into LI Module

  // Start -- Helper functions for maps
  const clearMapCache = (view = 'ALL') => {
    dispatch({ type: 'CLEAR_MAP_CACHE', view: view });
  };

  const clearAllMap = () => {
    dispatch({ type: 'CLEAR_MAP' });
  };

  // Start -- Initial Render for map view click
  //// -- fetch map layouts
  const fetchMap = async ({
    filter,
    reportOwner,
    type = LITYPE,
    view = 'FSA',
    invalidateCache = false,
  } = {}) => {
    // dispatch({ type: 'CLEAR_MAP' });
    if (invalidateCache) {
      dispatch({ type: 'CLEAR_MAP_CACHE', view: 'ALL' });
    }
    if (!invalidateCache && state.mapDataCache[view]) {
      dispatch({
        type: 'FETCH_MAP',
        payload: state.mapDataCache[view],
        view: view,
      });
      return state.mapDataCache[view];
    } else {
      const data = await fetchMapInfo({
        filter: filter,
        reportOwner: reportOwner,
        type: type,
        view: view,
      });
      return data;
    }
  };

  const fetchMapInfo = async ({ filter, reportOwner, type, view }) => {
    try {
      loadingIncrement(type);
      filter = { ...filter, ids: mainContext.ids };
      const token = await authContext.getIdToken();
      if (view === 'Postal' || view === 'Census') {
        const res = await axios.post(
          '/search/point_info',
          {
            ...filter,
            reportOwner: reportOwner,
            view: view,
            countryCode: authContext.userCountryCode,
            kwargs: getPostKWArgs(),
          },
          {
            headers: { Authorization: token },
          }
        );
        dispatch({
          type: 'FETCH_MAP',
          payload: res.data,
          view: view,
        });
        return res.data;
      } else {
        const res = await axios.post(
          '/search/polygon_info',
          {
            ...filter,
            reportOwner: reportOwner,
            view: view === 'None' ? 'FSA' : view,
            countryCode: authContext.userCountryCode,
            kwargs: getPostKWArgs(),
          },
          {
            headers: { Authorization: token },
          }
        );
        dispatch({
          type: 'FETCH_MAP',
          payload: res.data,
          view: view,
        });
        return res.data;
      }
    } catch (err) {
      if (err.response) {
        handleError(err);
      }
    } finally {
      loadingDecrement(type);
    }
  };

  //// -- fetch heat map info
  const fetchMobilityMap = async ({
    filter,
    reportOwner,
    type = LITYPE,
    invalidateCache = false,
  } = {}) => {
    if (invalidateCache) {
      dispatch({ type: 'CLEAR_MAP_CACHE', view: 'ALL' });
    }
    if (!invalidateCache && state.mapDataCache['Mobility']) {
      dispatch({
        type: 'FETCH_HEATMAP',
        payload: state.mapDataCache['Mobility'],
        view: 'Mobility',
      });
      return state.mapDataCache['Mobility'];
    } else {
      return await fetchMobilityMapInfo({
        filter: filter,
        reportOwner: reportOwner,
        type: type,
      });
    }
  };

  const fetchMobilityMapInfo = async ({ filter, reportOwner, type }) => {
    try {
      loadingIncrement(type);
      filter = { ...filter, ids: mainContext.ids };
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/search/heatmap_info',
        {
          ...filter,
          reportOwner: reportOwner,
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      dispatch({
        type: 'FETCH_HEATMAP',
        payload: res.data,
        view: 'Mobility',
      });
      return res.data;
    } catch (err) {
      if (err.response) {
        handleError(err);
      }
    } finally {
      loadingDecrement(type);
    }
  };

  //// -- fetch flow map info
  const fetchFlowMap = async ({
    reportOwner,
    filter,
    type = LITYPE,
    invalidateCache = false,
  } = {}) => {
    if (invalidateCache) {
      dispatch({ type: 'CLEAR_MAP_CACHE', view: 'ALL' });
    }
    if (!invalidateCache && state.mapDataCache['Flow']) {
      dispatch({
        type: 'FETCH_FLOWMAP',
        payload: state.mapDataCache['Flow'],
        view: 'Flow',
      });
      return state.mapDataCache['Flow'];
    } else {
      return await fetchFlowMapInfo({
        filter: filter,
        reportOwner: reportOwner,
        type: type,
      });
    }
  };

  const fetchFlowMapInfo = async ({ filter, reportOwner, type }) => {
    try {
      loadingIncrement(type);
      filter = { ...filter, ids: mainContext.ids };
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/search/flow_map',
        {
          ...filter,
          reportOwner: reportOwner,
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      dispatch({
        type: 'FETCH_FLOWMAP',
        payload: res.data,
        view: 'Flow',
      });
      return res.data;
    } catch (err) {
      if (err.response) {
        handleError(err);
      }
    } finally {
      loadingDecrement(type);
    }
  };

  // bus routes

  const getKMLGeojson = async (routes, type = LITYPE) => {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/get_kml_geojson',
        {
          route: routes,
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      delete res.data.status_code;
      dispatch({
        type: 'GET_KML_GEOJSON',
        payload: res.data,
      });
      return res.data;
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  };

  // ----- PANEL APIS -----

  const getOOHPanelData = async () => {
    try {
      dispatch({
        type: 'GET_OOH_PANELS',
        payload: { requestStatus: 'PENDING' },
      });
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/extract_ooh_panels',
        {
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
        },
        { headers: { Authorization: token } }
      );
      dispatch({
        type: 'GET_OOH_PANELS',
        payload: { ...res.data, requestStatus: 'SUCCESS' },
      });
    } catch (err) {
      if (err.response) {
        handleError(err);
        dispatch({
          type: 'GET_OOH_PANELS',
          payload: { requestStatus: 'ERROR' },
        });
      }
    }
  };

  const getOOHResults = async ({
    OOHPanelData = {},
    isVendor = false,
    type = LITYPE,
  }) => {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/get_ooh_results',
        {
          OOHPanelData: OOHPanelData,
          isVendor: isVendor,
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
        },
        { headers: { Authorization: token } }
      );
      dispatch({
        type: 'GET_OOH_RESULTS',
        payload: res.data,
      });
      return res.data;
    } catch (err) {
      if (err.response) {
        handleError(err);
      }
    } finally {
      loadingDecrement(type);
    }
  };

  const extractOOH = async ({
    oohType,
    companies,
    panels,
    uidArr,
    bounds,
    isVendor = false,
    mobilityView = false,
    aidTable = null,
    fsaConcentrations = null,
    totalPopulation = null,
    dateFilters = null,
    type = LITYPE,
  } = {}) => {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        `/extract_OOH_location`,
        {
          // oohType: oohType,
          companies: companies,
          panels: panels,
          uidArr: uidArr,
          bounds: bounds,
          isVendor: isVendor,
          mobilityView: mobilityView,
          aidTable: aidTable,
          fsaConcentrations: fsaConcentrations,
          totalPopulation: totalPopulation,
          dateFilters: dateFilters,
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      const oohTable = res.data.product_type.map((board, i) => ({
        panel: res.data.panel[i],
        Address: res.data.address[i],
        product_type: board,
        media_type_name: board,
        company: res.data.company[i],
        Longitude: res.data.lng[i],
        Latitude: res.data.lat[i],
        uid: res.data.uid[i],
        uids_at_location: res.data.uids_at_location[i],
        panels_at_location: res.data.panels_at_location[i],
      }));

      const oohGeojson = {
        type: 'FeatureCollection',
        features: res.data.product_type.map((board, i) => {
          const dynamicProperties = {};
          Object.keys(res.data.additional_columns).forEach((key) => {
            dynamicProperties[key] = res.data.additional_columns[key][i];
          });

          return {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [res.data.lng[i], res.data.lat[i]],
            },
            properties: {
              product_type: board,
              media_type_name: board,
              lng: res.data.lng[i],
              lat: res.data.lat[i],
              panel: res.data.panel[i],
              company: oohType,
              companyName: res.data.company[i],
              audience_traffic: res.data.audience_traffic[i],
              address: res.data.address[i],
              category: board,
              dynamicProperties: dynamicProperties,
              uid: res.data.uid[i],
              uids_at_location: res.data.uids_at_location[i],
              panels_at_location: res.data.panels_at_location[i],
            },
          };
        }),
      };
      const uniqueCompanies = Array.from(new Set(res.data.company));

      dispatch({
        type: 'EXTRACT_OOH',
        payload: {
          oohType: oohType,
          ooh: {
            oohGeojson: oohGeojson,
            oohTable: oohTable,
            selected: [...Array(res.data.panel.length).keys()],
            companies: uniqueCompanies,
          },
        },
      });
      return oohTable;
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  };

  const getStoreName = async () => {
    try {
      dispatch({
        type: 'GET_STORE_NAME',
        payload: { requestStatus: 'PENDING' },
      });
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/get_store_name',
        {
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      dispatch({
        type: 'GET_STORE_NAME',
        payload: { ...res.data, requestStatus: 'SUCCESS' },
      });
    } catch (err) {
      if (err.response) {
        handleError(err);
        dispatch({
          type: 'GET_STORE_NAME',
          payload: { requestStatus: 'ERROR' },
        });
      }
    }
  };

  const extractStores = async ({
    storeType,
    poiName,
    bounds,
    includeAudienceMetrics = false,
    type = LITYPE,
  } = {}) => {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        `/extract_stores`,
        {
          bounds: bounds,
          storeType: storeType,
          includeAudienceMetrics: includeAudienceMetrics,
          ids: includeAudienceMetrics ? mainContext.ids : undefined,
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      const storeLocation = res.data.brand_name.map((store, i) => ({
        Brand: store,
        fullName: res.data.full_name[i],
        Address: res.data.address[i],
        City: res.data.city[i],
        Province: res.data.province[i],
        Postcode: res.data.postcode[i],
        Longitude: res.data.lng[i],
        Latitude: res.data.lat[i],
        ...(includeAudienceMetrics && {
          'Density (%)': res.data.audience_density[i],
          'Index (%)': res.data.audience_index[i],
        }),
      }));
      const storeGeojson = {
        type: 'FeatureCollection',
        features: res.data.brand_name.map((store, i) => {
          return {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [res.data.lng[i], res.data.lat[i]],
            },
            properties: {
              store: res.data.brand_name[i],
              overlayName: poiName,
              lng: res.data.lng[i],
              lat: res.data.lat[i],
              brand: res.data.brand_name[i],
              address: res.data.address[i],
              category: res.data.category[i],
              custom_location_name: res.data.custom_location_name[i],
            },
          };
        }),
      };

      return {
        storeGeojson,
        storeLocation,
        storeNames: storeType,
        selected: [...Array(res.data.brand_name.length).keys()],
      };
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  };

  /**
   * Fetches and updates the store data based on the provided parameters, then dispatches an action with the extracted store details.
   *
   * @param {Object} params - The parameters for updating the stores.
   * @param {string} params.storeType - The type of store to filter the data (e.g., "grocery", "electronics").
   * @param {string} params.poiName - The name of the point of interest (POI) to filter by.
   * @param {Array} params.bounds - The geographical bounds to filter stores within.
   * @param {string} [params.type=LITYPE] - An optional type, defaulting to `LITYPE`.
   * @returns {Object|null} The extracted store location data, or `null` if no data is found.
   */
  const updateExtractedStores = async ({
    storeType,
    poiName,
    bounds,
    includeAudienceMetrics = false,
    type = LITYPE,
  } = {}) => {
    const extractedStoreData = await extractStores({
      storeType: storeType,
      poiName: poiName,
      bounds: bounds,
      includeAudienceMetrics,
      type,
    });

    if (!extractedStoreData) return null;

    dispatch({
      type: 'EXTRACT_STORES',
      payload: {
        storeType: storeType,
        poiName: poiName,
        stores: {
          storeGeojson: extractedStoreData.storeGeojson,
          storeTable: extractedStoreData.storeLocation,
          selected: extractedStoreData.selected,
          storeNames: storeType,
        },
      },
    });

    return extractedStoreData.storeLocation;
  };

  const clearStoreData = () => {
    dispatch({ type: 'CLEAR_STORE_DATA' });
  };

  const getDriveTime = async (coordinate, routing, time, type = LITYPE) => {
    try {
      // this api call is fast enough to not need a loading increment
      // but loading increment can be useful for user visual
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/get_drive_time_layer',
        {
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
          coordinate: coordinate,
          routing: routing,
          time: time,
        },
        {
          headers: { Authorization: token },
        }
      );
      dispatch({
        type: 'GET_DRIVE_TIME_LAYER',
        payload: res.data,
      });
      return res.data;
    } catch (err) {
      handleError(err);
    }
  };

  const clearDriveTime = () => {
    dispatch({ type: 'CLEAR_DRIVE_LAYER' });
  };

  const getOOHAnalytics = async ({
    mobilityView = false,
    aidTable = null,
    fsaConcentrations = null,
    totalPopulation = null,
    dateFilters = null,
    bounds,
    companies,
    panels,
    lng,
    lat,
    uid,
    type = LITYPE,
  } = {}) => {
    try {
      const uidKey = `${uid}`;
      const dateKey = `${dateFilters[0]}_${dateFilters[1]}`;
      if (state.oohAnalytics[uidKey] && state.oohAnalytics[uidKey][dateKey]) {
        return;
      }
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/get_ooh_analytics',
        {
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
          mobilityView: mobilityView,
          aidTable: aidTable,
          fsaConcentrations: fsaConcentrations,
          totalPopulation: totalPopulation,
          dateFilters: dateFilters,
          bounds: bounds,
          companies: companies,
          panels: panels,
          lons: lng,
          lats: lat,
          ids: mainContext.ids,
        },
        {
          headers: { Authorization: token },
        }
      );
      const updatedAnalytics = {
        ...state.oohAnalytics,
        [uidKey]: {
          ...(state.oohAnalytics[uidKey] || {}),
          [dateKey]: res.data,
        },
      };

      dispatch({
        type: 'GET_OOH_ANALYTICS',
        payload: updatedAnalytics,
      });
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  };

  const clearOOHAnalytics = () => {
    dispatch({ type: 'CLEAR_OOH_ANALYTICS' });
  };

  const getRetailAnalytics = async ({
    mobilityView = false,
    aidTable = null,
    fsaConcentrations = null,
    totalPopulation = null,
    dateFilters = null,
    bounds,
    companies,
    panels,
    lng,
    lat,
    type = LITYPE,
  } = {}) => {
    try {
      const uidKey = `${lng[0]},${lat[0]}`;
      const dateKey = `${dateFilters[0]}_${dateFilters[1]}`;
      if (state.poiAnalytics[uidKey] && state.poiAnalytics[uidKey][dateKey]) {
        return;
      }
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/get_retail_analytics',
        {
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
          mobilityView: mobilityView,
          aidTable: aidTable,
          fsaConcentrations: fsaConcentrations,
          totalPopulation: totalPopulation,
          dateFilters: dateFilters,
          bounds: bounds,
          companies: companies,
          panels: panels,
          lons: lng,
          lats: lat,
          ids: mainContext.ids,
        },
        {
          headers: { Authorization: token },
        }
      );

      const updatedAnalytics = {
        ...state.poiAnalytics,
        [uidKey]: {
          ...(state.poiAnalytics[uidKey] || {}),
          [dateKey]: res.data,
        },
      };

      dispatch({
        type: 'GET_RETAIL_ANALYTICS',
        payload: updatedAnalytics,
      });
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  };

  const clearRetailAnalytics = () => {
    dispatch({ type: 'CLEAR_RETAIL_ANALYTICS' });
  };

  const getRetailFlowMap = async ({
    mobilityView = false,
    aidTable = null,
    fsaConcentrations = null,
    totalPopulation = null,
    dateFilters = null,
    bounds,
    companies,
    panels,
    lons,
    lats,
    poiProperties,
    poiType,
    type = LITYPE,
  } = {}) => {
    try {
      loadingIncrement(type);
      dispatch({
        type: 'CURRENT_PANEL',
        payload: poiProperties,
      });
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/search/retail_flow_map',
        {
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
          mobilityView: mobilityView,
          aidTable: aidTable,
          fsaConcentrations: fsaConcentrations,
          totalPopulation: totalPopulation,
          dateFilters: dateFilters,
          bounds: bounds,
          companies: companies,
          panels: panels,
          lons: lons,
          lats: lats,
          poiType: poiType,
          ids: mainContext.ids,
        },
        {
          headers: { Authorization: token },
        }
      );
      dispatch({
        type: 'GET_RETAIL_FLOWMAP',
        payload: res.data,
      });
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  };

  const setReportRetailFlow = (reportData, poiProperties) => {
    dispatch({
      type: 'SET_RETAIL_FLOWMAP',
      payload: reportData,
    });
    dispatch({
      type: 'CURRENT_PANEL',
      payload: poiProperties,
    });
  };

  const populateStoreSpreadsheet = async (brandName) => {
    try {
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/populate_store_spreadsheet',
        {
          brand_name: brandName,
          countryCode: authContext.userCountryCode,
          kwargs: mainContext.getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      return res.data;
    } catch (err) {
      handleError(err);
    }
  };

  const checkSelectedStore = async (brandName) => {
    try {
      loadingIncrement(LITYPE);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/check_existing_store',
        {
          brand_name: brandName,
          countryCode: authContext.userCountryCode,
          kwargs: mainContext.getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      return res.data;
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(LITYPE);
    }
  };

  const uploadCustomStores = async (
    newStores,
    storeName,
    category,
    oldStores,
    edit
  ) => {
    try {
      loadingIncrement(LITYPE);
      const token = await authContext.getIdToken();

      const newData = newStores.map((store) => ({
        name: storeName, // Add storeName
        address: store.address,
        city: store.city,
        province: store.province,
        postcode: store.postalCode,
        custom_location_name: store.customName ? store.customName : "",
        geometry: null,
        size: 'medium',
        owner: store.owner,
        category: category,
      }));

      await axios.post(
        '/custom_store_upload',
        {
          countryCode: authContext.userCountryCode,
          newStores: newData,
          oldStores: oldStores,
          edit: edit,
          kwargs: getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      return true;
    } catch (err) {
      if (err.response?.data?.status_code === 400) {
        return err.response.data.status;
      } else {
        handleError(err);
        return 'An unexpected error occurred';
      }
    } finally {
      loadingDecrement(LITYPE);
    }
  };

  const updatePopupView = (view) => {
    dispatch({ type: 'POPUP_VIEW', payload: view });
  };

  const clearPopupView = () => {
    dispatch({ type: 'CLEAR_POPUP_VIEW' });
  };

  async function extractFSA({ oohLocations, type = CSVTYPE } = {}) {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/extract_partial_csv',
        {
          ids: mainContext.ids,
          oohLocations: oohLocations,
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
        },
        { headers: { Authorization: token } }
      );

      openPopup(res.data.status);
    } catch (err) {
      if (err.message === 'Pop-ups are blocked') {
        throw err;
      }

      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  }

  const extractOOHWithinStore = async ({
    stores,
    oohs,
    radius,
    type = LITYPE,
  } = {}) => {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/extract_ooh_within_store',
        {
          stores: stores,
          oohs: oohs,
          radius: radius,
          ids: mainContext.ids,
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );

      openPopup(res.data.status);
    } catch (err) {
      if (err.message === 'Pop-ups are blocked') {
        throw err;
      }

      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  };

  const extractFSAWithinStore = async ({
    stores,
    oohs,
    radius,
    type = LITYPE,
  } = {}) => {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/extract_fsa_within_store',
        {
          stores: stores,
          oohs: oohs,
          radius: radius,
          ids: mainContext.ids,
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );

      openPopup(res.data.status);
    } catch (err) {
      if (err.message === 'Pop-ups are blocked') {
        throw err;
      }
      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  };

  const extractTopPOI = async ({
    stores,
    oohs,
    dateFilters,
    numLocations,
    selectedLocation,
    bounds,
    clientEmail,
    fileName,
    type = LITYPE,
  } = {}) => {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      await axios.post(
        '/extract_top_poi',
        {
          stores: stores,
          oohs: oohs,
          numLocations: numLocations,
          dateFilters: dateFilters,
          selectedLocation: selectedLocation,
          ids: mainContext.ids,
          countryCode: authContext.userCountryCode,
          bounds: bounds,
          clientEmail: clientEmail,
          fileName: fileName,
          kwargs: getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  };

  async function extractCSV({ features, type = CSVTYPE } = {}) {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/extract_csv',
        {
          ids: mainContext.ids,
          features: features || [],
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
        },
        { headers: { Authorization: token } }
      );

      openPopup(res.data.status);
    } catch (err) {
      if (err.message === 'Pop-ups are blocked') {
        throw err;
      }

      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  }

  ////////////
  // Tracking

  /**
   * Captures an event for tracking purposes. If the event is a workflow start event,
   * it generates a workflowId and stores it. If it's a workflow end event, it clears
   * the stored workflowId after sending the event.
   *
   * Properties we care about are "workflow_id". captureEvent will try to
   * append them from state.workflowId and state.tabWorkflowId, or generate new ones for start events.
   *
   * @param {string} eventName - The name of the event to track
   * @param {Object} extraProperties - Additional properties to include with the event
   * @returns {Object} - Returns the event properties
   */
  const captureEvent = (eventName, extraProperties = {}) => {
    let workflowId = state.workflowId;

    // Workflow IDs identify a sequence of events. We start a sequence on START and end it on EXIT.
    // So we set/unset the workflow ID on START and EXIT.
    if (eventName === EVENT_NAMES.LOCINTELLIGENCE_START) {
      workflowId = uuidv4();
      dispatch({ type: 'SET_WORKFLOW_ID', payload: workflowId });
    } else if (eventName === EVENT_NAMES.LOCINTELLIGENCE_EXIT) {
      dispatch({ type: 'CLEAR_WORKFLOW_ID' }); // clear for next events
    }

    // Capture the event
    const eventProps = {
      workflow_id: workflowId,
      ...extraProperties,
    };

    if (eventProps.workflow_id) {
      // Only capture events if we have a workflow ID
      // Reports don't have workflow IDs, and we don't want to track those here
      trackerService.captureEvent(eventName, authContext.userInfo, eventProps);
    }
    return eventProps;
  };

  return (
    <LIContext.Provider
      value={{
        requestStatus: state.requestStatus,
        driveTimeLayer: state.driveTimeLayer,
        mapData: state.mapData,
        mapDataCache: state.mapDataCache,
        mobilityDates: state.mobilityDates,
        geoLocation: state.geoLocation,
        oohData: state.oohData,
        oohPanelData: state.oohPanelData,
        oohResults: state.oohResults,
        storeName: state.storeName,
        topStoreName: state.topStoreName,
        storeData: state.storeData,
        kmlData: state.kmlData,
        poiAnalytics: state.poiAnalytics,
        oohAnalytics: state.oohAnalytics,
        retailFlowMap: state.retailFlowMap,
        currPanel: state.currPanel,
        popupView: state.popupView,
        storeCategories: state.storeCategories,
        singleCategories: state.singleCategories,
        brandNames: state.brandNames,
        workflowId: state.workflowId,
        clearStoreData,
        clearAllMap,
        clearDriveTime,
        clearMapCache,
        clearRetailAnalytics,
        clearOOHAnalytics,
        fetchMap,
        fetchInitialDates,
        fetchMobilityMap,
        fetchFlowMap,
        getDriveTime,
        getGeoLocation,
        getOOHPanelData,
        getOOHResults,
        getRetailAnalytics,
        getRetailFlowMap,
        getOOHAnalytics,
        getStoreName,
        getKMLGeojson,
        setGeoLocation,
        extractOOH,
        extractStores,
        updateExtractedStores,
        updatePopupView,
        uploadCustomStores,
        clearPopupView,
        populateStoreSpreadsheet,
        checkSelectedStore,
        extractOOHWithinStore,
        extractFSAWithinStore,
        extractTopPOI,
        extractFSA,
        extractCSV,
        setReportRetailFlow,
        refresh,
        captureEvent,
      }}
    >
      {props.children}
    </LIContext.Provider>
  );
}

export default LIState;
